import DOMPurify, { type Config } from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 *
 * Uses DOMPurify to clean HTML from NBC API and other sources.
 *
 * @param html - Raw HTML string from API (can be null/undefined)
 * @returns Sanitized HTML safe for v-html rendering
 *
 * @example
 * // Basic usage
 * const clean = sanitizeHtml('<p>Hello <script>alert("xss")</script></p>');
 * // Returns: '<p>Hello </p>'
 *
 * @example
 * // In Vue component
 * const sanitizedContent = computed(() => sanitizeHtml(props.htmlDescription));
 * // <div v-html="sanitizedContent"></div>
 *
 * @example
 * // Handles null/undefined
 * sanitizeHtml(null);      // Returns: ''
 * sanitizeHtml(undefined); // Returns: ''
 * sanitizeHtml('');        // Returns: ''
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';

  const config: Config = {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'a',
      'span',
      'div',
      'blockquote',
      'small',
    ],

    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class'],

    ALLOW_DATA_ATTR: false,

    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };

  return DOMPurify.sanitize(html, config) as string;
}

/**
 * Sanitize HTML intended for inline use inside translations.
 *
 * Allows only inline formatting and links — meant for short translated strings
 * (labels, terms & conditions, GDPR links) rendered via `v-html`. Block-level
 * tags (p, div, lists, headings) are stripped; use {@link sanitizeHtml} for those.
 *
 * Runs AFTER interpolation, so any interpolated values (e.g. URLs) are cleaned too.
 *
 * @param html - Raw HTML string (can be null/undefined)
 * @returns Sanitized HTML safe for v-html rendering
 *
 * @example
 * sanitizeInlineHtml('I accept the <a href="/gtc">terms</a> and <strong>conditions</strong>.');
 * sanitizeInlineHtml('<span onclick="x()">hi</span>'); // Returns: '<span>hi</span>'
 */
export function sanitizeInlineHtml(html: string | null | undefined): string {
  if (!html) return '';

  const config: Config = {
    ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'u', 'a', 'br', 'span'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };

  return DOMPurify.sanitize(html, config) as string;
}
